import { jwtDecode } from "jwt-decode";

/** Stamped on the access token by the Auth0 action (`auth0/actions/javascript/UserAttributes.js`). */
type LaraClaims = {
  "https://markup.legal/user_id"?: unknown;
  "https://markup.legal/organization_id"?: unknown;
};

export type TelemetryUser = {
  /** The Lara user id, a GUID. */
  userId: string;
  /** The Lara organization id (the tenant), a GUID. See CONTEXT.md. */
  tenantId?: string;
};

const nonEmptyString = (value: unknown): string | undefined =>
  typeof value === "string" && value !== "" ? value : undefined;

/**
 * The Lara user and organization an access token was issued for, or null without a usable token.
 *
 * Lara's own ids, not the Auth0 `sub`: they are what the API and its database record, so a browser
 * row leads straight to the user's data — and App Insights drops an id containing `|`, which every
 * `sub` does.
 */
export const telemetryUserFromAccessToken = (
  token: string | null | undefined
): TelemetryUser | null => {
  if (!token) return null;

  let claims: LaraClaims;
  try {
    claims = jwtDecode<LaraClaims>(token);
  } catch {
    // Telemetry just goes untagged; the token's own failures surface where it is used.
    return null;
  }

  const userId = nonEmptyString(claims["https://markup.legal/user_id"]);
  if (!userId) return null;

  return {
    userId,
    tenantId: nonEmptyString(claims["https://markup.legal/organization_id"]),
  };
};

type AuthenticatedUserContext = {
  setAuthenticatedUserContext: (
    authenticatedUserId: string,
    accountId?: string
  ) => void;
  clearAuthenticatedUserContext: () => void;
};

type AccessTokenStore = {
  getState: () => { token: string };
  subscribe: (listener: (state: { token: string }) => void) => () => void;
};

/**
 * Keeps App Insights' authenticated user (`user_AuthenticatedId`) and account (`user_AccountId`)
 * on the Lara user and organization of the stored access token (LAR-520): set from the persisted
 * token at startup, moved when a refresh or an organization switch stores a new one, and cleared
 * when logout empties the store. Returns the unsubscribe.
 */
export const syncTelemetryUserContext = (
  appInsights: AuthenticatedUserContext,
  tokenStore: AccessTokenStore
): (() => void) => {
  let appliedToken: string | undefined;
  let applied: TelemetryUser | null = null;

  const apply = (token: string) => {
    // The store also changes for permissions, and a refresh re-stores the same claims.
    if (token === appliedToken) return;
    appliedToken = token;

    const next = telemetryUserFromAccessToken(token);
    if (
      next?.userId === applied?.userId &&
      next?.tenantId === applied?.tenantId
    ) {
      return;
    }

    // Clear first: setting a user without an account would keep the previous account.
    if (applied) appInsights.clearAuthenticatedUserContext();
    applied = next;
    if (next)
      appInsights.setAuthenticatedUserContext(next.userId, next.tenantId);
  };

  apply(tokenStore.getState().token);
  return tokenStore.subscribe((state) => apply(state.token));
};
